/**
 * Confetti celebration for authoring a training module.
 *
 * Mirrors the SOP / props celebration helpers: a module-level
 * ``prefers-reduced-motion`` guard plus ``disableForReducedMotion: true``
 * passed to ``canvas-confetti`` (belt-and-braces per the AGENTS.md
 * gamification guidance).
 *
 * Note: *finishing* a training is deliberately NOT confetti-tier — it's a
 * routine completion, celebrated with a toast instead. Confetti is reserved
 * for rarer wins (see the celebration-tier guidance in /styleguide).
 */

import confetti from "canvas-confetti";

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Match the broader Hickory accent palette used by the SOP / props
 *  celebrations so the visual language stays consistent. */
const COLORS = ["#22C55E", "#4ADE80", "#fbbf24", "#F97316", "#3B82F6"];

/** Fired when an admin imports a brand-new training module. A modest
 *  burst (smaller than Finish) — creating content is meaningful but
 *  authoring is a repeatable admin action, not a once-per-module win. */
export function triggerModuleImported(): void {
  if (REDUCED_MOTION) return;
  confetti({
    particleCount: 45,
    spread: 70,
    origin: { y: 0.7 },
    colors: COLORS,
    gravity: 1.1,
    scalar: 0.95,
    ticks: 140,
    disableForReducedMotion: true,
  });
}

/** Fired when a learner passes a module's quiz — demonstrated competence
 *  clearing a gate, a tier above the routine module Finish (which stays
 *  toast-only). Failing an attempt never celebrates. */
export function triggerQuizPassed(): void {
  if (REDUCED_MOTION) return;
  confetti({
    particleCount: 70,
    spread: 75,
    origin: { y: 0.6 },
    colors: COLORS,
    gravity: 1.0,
    scalar: 1,
    ticks: 160,
    disableForReducedMotion: true,
  });
}
